import pLimit from "p-limit";

import {
  buildDependencyRows,
  collectLineageDependents,
  DEPENDENCY_EXPORT_HEADERS,
  type LineageExportNode,
  type LineageExportRef,
  type ReachedDependent,
} from "src/components/lineage/lineage-export";

// Enough to overlap round trips without one export crowding out the rest of the page.
const USAGE_EXPORT_CONCURRENCY = 6;

export type UsageDependentsPage = {
  rows: ReadonlyArray<{
    node: LineageExportNode;
    directDependentCount: number;
  }>;
  pageInfo: { hasNextPage: boolean; endCursor: string | null };
};

export type LoadUsageDependentsPage = (
  args: LineageExportRef & { cursor: string | null },
) => Promise<UsageDependentsPage>;

async function loadAllDependents(
  parent: LineageExportRef,
  loadPage: LoadUsageDependentsPage,
): Promise<UsageDependentsPage["rows"]> {
  const rows: UsageDependentsPage["rows"][number][] = [];
  let cursor: string | null = null;
  while (true) {
    const page = await loadPage({
      resourceType: parent.resourceType,
      resourceId: parent.resourceId,
      cursor,
    });
    rows.push(...page.rows);
    if (!page.pageInfo.hasNextPage) {
      return rows;
    }
    if (!page.pageInfo.endCursor) {
      throw new Error("lineage dependents page is missing its cursor");
    }
    cursor = page.pageInfo.endCursor;
  }
}

/**
 * Every resource downstream of the root, read page by page through the API.
 * Only resources with dependents of their own are read, so leaves cost no request.
 */
export function collectUsageDependents({
  root,
  loadPage,
  maxDepth,
}: {
  root: LineageExportRef;
  loadPage: LoadUsageDependentsPage;
  maxDepth?: number;
}): Promise<ReachedDependent<LineageExportNode>[]> {
  const limit = pLimit(USAGE_EXPORT_CONCURRENCY);
  return collectLineageDependents({
    root,
    maxDepth,
    readDependents: (parents) =>
      Promise.all(
        parents.map((parent) =>
          limit(async () =>
            (await loadAllDependents(parent, loadPage)).map((row) => ({
              node: row.node,
              hasDependents: row.directDependentCount > 0,
            })),
          ),
        ),
      ),
  });
}

/** Header row first, then one row per resource downstream of the root. */
export async function buildResourceUsageRows({
  root,
  loadPage,
  resourceUrl,
}: {
  root: LineageExportRef;
  loadPage: LoadUsageDependentsPage;
  /** Absolute link to a resource, or an empty string when nothing addresses it. */
  resourceUrl: (node: LineageExportNode) => string;
}): Promise<string[][]> {
  const dependents = await collectUsageDependents({ root, loadPage });
  return [
    DEPENDENCY_EXPORT_HEADERS,
    // The read returns the root's dependents, never the root itself, so there is
    // no name or link for the root.
    ...buildDependencyRows({
      root: { ...root, name: null, url: "" },
      dependents,
      resourceUrl,
    }),
  ];
}
